import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Users, Trophy, Shield, ChevronRight, Award } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import { coaches } from "@/pages/CoachesPage";

const IMAGES = {
  hero: "/volleyball-celebration.jpg",
  volleyball: "/volleyball-highfive.jpg",
  basketball: "/basketball-dribble.jpg",
  gymnastics: "/gymnastics-splits.jpg",
  kids: "/summer-outdoor-fun.jpg",
};

const branches = [
  {
    name: "Voleybol",
    slug: "voleybol",
    image: IMAGES.volleyball,
    desc: "Takim ruhu ve teknik becerilerle voleybolun keyfini yasayin.",
  },
  {
    name: "Basketbol",
    slug: "basketbol",
    image: IMAGES.basketball,
    desc: "Dinamik antrenmanlarla basketbolda kendinizi gelistirin.",
  },
  {
    name: "Cimnastik",
    slug: "cimnastik",
    image: IMAGES.gymnastics,
    desc: "Esneklik, guc ve koordinasyon ile cimnastikte parlayin.",
  },
];

const stats = [
  { number: "100+", label: "Aktif Sporcu", icon: Users },
  { number: "3", label: "Spor Branşı", icon: Trophy },
  { number: "10+", label: "Yıllık Deneyim", icon: Shield },
];

export default function HomePage() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "SportsOrganization",
    "name": "İRÜ Spor Kulübü",
    "alternateName": "IRU Spor Kulubu",
    "url": "https://irusporkulubu.com",
    "logo": "https://irusporkulubu.com/rumeli-logo.png",
    "description": "İstanbul Rumeli Üniversitesi Bostancı Yerleşkesi'nde voleybol, basketbol ve cimnastik eğitimi veren profesyonel spor kulübü. Ataşehir, Kadıköy, Maltepe bölgesinde hizmet vermektedir.",
    "sport": ["Voleybol", "Basketbol", "Cimnastik"],
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Bostancı Yerleşkesi",
      "addressLocality": "İstanbul",
      "addressRegion": "Kadıköy",
      "addressCountry": "TR"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 40.9638,
      "longitude": 29.0958
    },
    "telephone": "+905436470183",
    "email": "info@irusporkulubu.com",
    "areaServed": [
      { "@type": "City", "name": "Ataşehir" },
      { "@type": "City", "name": "Kadıköy" },
      { "@type": "City", "name": "Maltepe" }
    ],
    "memberOf": [
      { "@type": "SportsOrganization", "name": "Türkiye Voleybol Federasyonu (TVF)" },
      { "@type": "SportsOrganization", "name": "Türkiye Basketbol Federasyonu (TBF)" },
      { "@type": "SportsOrganization", "name": "Türkiye Cimnastik Federasyonu (TGF)" }
    ]
  };

  return (
    <div data-testid="home-page">
      <SEOHead
        title="Ataşehir, Kadıköy, Maltepe Voleybol, Basketbol, Cimnastik Spor Okulu"
        description="İRÜ Spor Kulübü - Ataşehir, Kadıköy, Maltepe bölgesinde çocuklar için voleybol, basketbol ve cimnastik eğitimi. TVF, TBF, TGF lisanslı kulüp. Anadolu yakası spor okulu. 5-14 yaş grubu için profesyonel antrenörler eşliğinde eğitim."
        keywords="spor okulu İstanbul, voleybol kursu Kadıköy, basketbol kursu Ataşehir, cimnastik kursu Maltepe, Anadolu yakası spor okulu, çocuk spor eğitimi, yaz spor okulu, İRÜ spor kulübü, Bostancı spor, lisanslı sporcu, voleybol eğitimi, basketbol eğitimi, cimnastik eğitimi, Rumeli Üniversitesi spor"
        canonical="/"
        jsonLd={jsonLd}
      />
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={IMAGES.hero}
            alt={"İRÜ Spor Kulübü - Ataşehir, Kadıköy, Maltepe Voleybol Basketbol Cimnastik Spor Okulu"}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A1A]/90 via-[#1A1A1A]/60 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-burgundy/20 border border-burgundy/30 px-4 py-1.5 mb-6 animate-fade-in">
              <span className="w-2 h-2 bg-gold rounded-full" />
              <span className="text-xs font-accent uppercase tracking-widest text-white/80">
                Rumeli Üniversitesi Güvencesiyle
              </span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white uppercase tracking-tight leading-[1.1] animate-fade-in-up">
              Geleceğin<br />
              <span className="text-gold">Şampiyonları</span><br />
              Burada Yetişiyor
            </h1>
            <p className="mt-6 text-base md:text-lg text-white/70 max-w-lg leading-relaxed opacity-0 animate-fade-in-up delay-200">
              {"Ataşehir, Kadıköy ve Maltepe bölgesinde, İstanbul Rumeli Üniversitesi Bostancı Yerleşkesi'nde profesyonel eğitmenler eşliğinde voleybol, basketbol ve cimnastik eğitimi."}
              5-14 yaş grubu için Voleybol, Basketbol ve Cimnastik branşları.
            </p>
            <p className="mt-3 text-sm text-gold font-medium opacity-0 animate-fade-in-up delay-200">
              Ücretsiz deneme antrenmanına katılabilirsiniz!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-10 opacity-0 animate-fade-in-up delay-300">
              <Link to="/on-kayit">
                <Button
                  data-testid="hero-register-btn"
                  className="bg-burgundy hover:bg-burgundy-700 text-white font-heading uppercase tracking-wider text-sm px-8 py-6"
                >
                  Hemen Kayıt Ol
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/branslarimiz">
                <Button
                  data-testid="hero-branches-btn"
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 font-heading uppercase tracking-wider text-sm px-8 py-6"
                >
                  Branşları İncele
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-burgundy py-12 lg:py-16" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {stats.map((stat, i) => (
              <div
                key={stat.label}
                className="flex items-center gap-4 justify-center sm:justify-start"
              >
                <div className="w-12 h-12 flex items-center justify-center border border-white/20">
                  <stat.icon className="w-5 h-5 text-gold" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-heading text-3xl font-bold text-white">{stat.number}</p>
                  <p className="text-sm font-accent text-white/70 uppercase tracking-wider">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Branches Preview */}
      <section className="py-24 lg:py-32 bg-white" data-testid="branches-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-16">
            <div>
              <span className="font-accent text-sm uppercase tracking-widest text-burgundy">Spor Branşları</span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-[#1A1A1A] mt-2">
                Branşlarımız
              </h2>
            </div>
            <Link to="/branslarimiz" className="mt-4 lg:mt-0 flex items-center gap-1 text-sm font-accent uppercase tracking-wider text-burgundy hover:text-burgundy-700 transition-colors">
              Tümü <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {branches.map((branch, i) => (
              <Link
                key={branch.name}
                to={`/branslarimiz/${branch.slug}`}
                data-testid={`branch-card-${branch.name.toLowerCase()}`}
                className="group relative overflow-hidden border border-zinc-200 hover:border-burgundy/50 hover:shadow-xl transition-all duration-500 hover:-translate-y-1"
              >
                <div className="aspect-video overflow-hidden">
                  <img
                    src={branch.image}
                    alt={branch.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-bold uppercase tracking-wider text-[#1A1A1A]">
                    {branch.name}
                  </h3>
                  <p className="mt-2 text-sm text-gray-500 leading-relaxed">
                    {branch.desc}
                  </p>
                  <div className="mt-4 flex items-center gap-1 text-xs font-accent uppercase tracking-wider text-burgundy opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    Detaylı Bilgi <ArrowRight className="w-3 h-3" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Summer School CTA */}
      <section className="relative py-24 lg:py-32 overflow-hidden" data-testid="summer-school-cta">
        <div className="absolute inset-0">
          <img src={IMAGES.kids} alt="Yaz Spor Okulu" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1A1A1A]/80" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-xl">
            <span className="font-accent text-sm uppercase tracking-widest text-gold">Yaz Programı</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-white mt-2">
              Yaz Spor Okulu
            </h2>
            <p className="mt-6 text-base text-white/70 leading-relaxed">
              5-14 yaş grubu için özel tasarlanmış yaz spor okulu programımızla çocuklarınız
              hem eğleniyor hem de spor yeteneklerini geliştiriyor.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <Link to="/yaz-spor-okulu">
                <Button
                  data-testid="summer-school-btn"
                  className="bg-gold hover:bg-yellow-600 text-[#1A1A1A] font-heading uppercase tracking-wider text-sm px-8 py-6"
                >
                  Programı İncele
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/on-kayit">
                <Button
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 font-heading uppercase tracking-wider text-sm px-8 py-6"
                >
                  Kayıt Ol
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Antrenör Kadrosu */}
      <section className="py-24 lg:py-32 bg-surface" data-testid="coaches-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-16">
            <div>
              <span className="font-accent text-sm uppercase tracking-widest text-burgundy">Profesyonel Kadro</span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-[#1A1A1A] mt-2">
                Antrenör Kadromuz
              </h2>
              <p className="mt-3 text-sm text-zinc-500 max-w-lg">
                Alanında uzman ve deneyimli antrenörlerimiz ile çocuklarınızın spor gelişimini en üst düzeye taşıyoruz.
              </p>
            </div>
            <Link to="/antrenorler" className="mt-4 lg:mt-0 flex items-center gap-1 text-sm font-accent uppercase tracking-wider text-burgundy hover:text-burgundy-700 transition-colors">
              Tüm Kadro <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {coaches.map((coach, idx) => (
              <Link
                key={idx}
                to="/antrenorler"
                className="bg-white border border-zinc-200 hover:border-burgundy/30 hover:shadow-lg transition-all duration-300 p-6 group"
              >
                <div className="w-16 h-16 bg-zinc-100 rounded-full flex items-center justify-center mb-4 group-hover:bg-burgundy/5 transition-colors">
                  <Users className="w-7 h-7 text-zinc-400 group-hover:text-burgundy transition-colors" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-[#1A1A1A]">{coach.name}</h3>
                <p className="text-xs text-burgundy font-medium mt-0.5">{coach.role}</p>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {coach.qualifications.slice(0, 1).map((q) => (
                    <span key={q} className="inline-flex items-center gap-1 text-[10px] bg-zinc-100 text-zinc-500 px-2 py-0.5">
                      <Award className="w-2.5 h-2.5 text-burgundy" strokeWidth={1.5} />
                      {q}
                    </span>
                  ))}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-24 lg:py-32 bg-surface" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="font-accent text-sm uppercase tracking-widest text-burgundy">Neden Biz?</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-[#1A1A1A] mt-2">
            Üniversitenin Güvencesi
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
            {[
              {
                title: "Profesyonel Kadro",
                desc: "Alanında uzman, lisanslı antrenörler eşliğinde kaliteli spor eğitimi.",
                icon: Users,
              },
              {
                title: "Modern Tesisler",
                desc: "Rumeli Üniversitesi Bostancı Yerleşkesi'ndeki son teknoloji spor tesisleri.",
                icon: Shield,
              },
              {
                title: "Başarı Odaklı",
                desc: "Bireysel gelişim ve takım başarısı odaklı sistematik eğitim programları.",
                icon: Trophy,
              },
            ].map((item) => (
              <div key={item.title} className="p-8 bg-white border border-zinc-200 hover:border-burgundy/30 hover:shadow-lg transition-all duration-300 text-left">
                <div className="w-12 h-12 flex items-center justify-center bg-burgundy/5 border border-burgundy/10 mb-6">
                  <item.icon className="w-5 h-5 text-burgundy" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-lg font-bold uppercase tracking-wider text-[#1A1A1A]">
                  {item.title}
                </h3>
                <p className="mt-3 text-sm text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 lg:py-32 bg-burgundy" data-testid="final-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-white">
            Çocuğunuzun Spor Yolculuğu<br />Burada Başlıyor
          </h2>
          <p className="mt-6 text-base text-white/70 max-w-lg mx-auto">
            Hemen ön kayıt formunu doldurun, sizinle en kısa sürede iletişime geçelim.
          </p>
          <Link to="/on-kayit">
            <Button
              data-testid="final-cta-btn"
              className="mt-10 bg-white text-burgundy hover:bg-gray-100 font-heading uppercase tracking-wider text-sm px-10 py-6"
            >
              Ön Kayıt Formu
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
